import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  FileText, 
  Download, 
  Plus, 
  Leaf, 
  ClipboardCheck, 
  Users,
  Calendar,
  Loader2,
  Eye,
  FileSpreadsheet,
  File
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const getReportCompany = (report) => {
  return report?.company_name || getCurrentUser()?.company_name || "—";
};

const REPORT_TYPES = [
  { 
    value: "carbon_summary", 
    label: "Carbon Summary", 
    icon: Leaf,
    description: "Overview of carbon emissions by scope"
  },
  { 
    value: "csrd_compliance", 
    label: "CSRD Compliance", 
    icon: ClipboardCheck,
    description: "Regulatory compliance status report"
  },
  { 
    value: "supplier_analysis", 
    label: "Supplier Analysis", 
    icon: Users,
    description: "Supplier sustainability metrics"
  },
];

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    period_start: "",
    period_end: "",
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`);
      setReports(response.data);
    } catch (error) {
      toast.error("Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async () => {
    if (!formData.name || !formData.type || !formData.period_start || !formData.period_end) {
      toast.error("Please fill in all fields");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/reports`, formData);
      toast.success("Report created successfully");
      setDialogOpen(false);
      setFormData({ name: "", type: "", period_start: "", period_end: "" });
      fetchReports();
      // Auto-open the new report
      setSelectedReport(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to create report");
    } finally {
      setCreating(false);
    }
  };

  const exportToExcel = (report) => {
    const company = getReportCompany(report);
    const meta = [
      { Field: "Company", Value: company },
      { Field: "Report Name", Value: report.name },
      { Field: "Report Type", Value: REPORT_TYPES.find(t => t.value === report.type)?.label || report.type },
      { Field: "Period Start", Value: report.period_start },
      { Field: "Period End", Value: report.period_end },
      { Field: "Generated", Value: new Date(report.created_at).toLocaleString() },
      { Field: "", Value: "" },
    ];
    const data = Object.entries(report.data).map(([key, value]) => ({
      Field: key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      Value: typeof value === "number" ? value.toFixed(2) : value,
    }));

    const ws = XLSX.utils.json_to_sheet([...meta, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${report.name.replace(/\s+/g, "_")}.xlsx`);
    toast.success("Excel file downloaded");
  };

  const exportToPDF = (report) => {
    const company = getReportCompany(report);
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(26, 46, 34);
    doc.text("Ledger Green", 20, 20);

    doc.setFontSize(13);
    doc.setTextColor(0, 200, 83);
    doc.text(company, 20, 28);

    doc.setFontSize(16);
    doc.setTextColor(26, 46, 34);
    doc.text(report.name, 20, 40);

    doc.setFontSize(10);
    doc.setTextColor(92, 107, 99);
    doc.text(`Period: ${report.period_start} to ${report.period_end}`, 20, 50);
    doc.text(`Generated: ${new Date(report.created_at).toLocaleString()}`, 20, 57);

    // Table data
    const tableData = Object.entries(report.data).map(([key, value]) => [
      key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      typeof value === "number" ? value.toFixed(2) : String(value),
    ]);

    autoTable(doc, {
      startY: 68,
      head: [["Metric", "Value"]],
      body: tableData,
      theme: "grid",
      headStyles: { fillColor: [26, 46, 34] },
      styles: { fontSize: 10 },
    });

    doc.save(`${report.name.replace(/\s+/g, "_")}.pdf`);
    toast.success("PDF file downloaded");
  };

  const exportToHTML = (report) => {
    const company = getReportCompany(report);
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${report.name} - Ledger Green Report</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #f2f7f4; padding: 40px; }
    .container { max-width: 800px; margin: 0 auto; background: white; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); overflow: hidden; }
    .header { background: linear-gradient(135deg, #1a2e22 0%, #2d4a3e 100%); color: white; padding: 40px; }
    .header h1 { font-size: 28px; margin-bottom: 8px; }
    .header p { opacity: 0.8; font-size: 14px; }
    .company { font-size: 16px; font-weight: 600; color: #81e6a8; margin-bottom: 6px; letter-spacing: 0.5px; text-transform: uppercase; }
    .meta { display: flex; gap: 24px; margin-top: 16px; font-size: 12px; opacity: 0.7; }
    .content { padding: 40px; }
    .content h2 { color: #1a2e22; font-size: 18px; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 2px solid #e0e7e3; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 16px; text-align: left; border-bottom: 1px solid #e0e7e3; }
    th { background: #f2f7f4; font-weight: 600; color: #1a2e22; }
    td:last-child { text-align: right; font-family: monospace; font-weight: 600; }
    tr:hover { background: rgba(0,200,83,0.05); }
    .footer { padding: 24px 40px; background: #f2f7f4; text-align: center; font-size: 12px; color: #5c6b63; }
    .badge { display: inline-block; padding: 4px 12px; background: #00c853; color: #1a2e22; border-radius: 20px; font-size: 12px; font-weight: 600; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="company">${company}</div>
      <span class="badge">${REPORT_TYPES.find(t => t.value === report.type)?.label || report.type}</span>
      <h1>${report.name}</h1>
      <p>Sustainability Report</p>
      <div class="meta">
        <span>Period: ${report.period_start} to ${report.period_end}</span>
        <span>Generated: ${new Date(report.created_at).toLocaleString()}</span>
      </div>
    </div>
    <div class="content">
      <h2>Report Data</h2>
      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          ${Object.entries(report.data).map(([key, value]) => `
            <tr>
              <td>${key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</td>
              <td>${typeof value === "number" ? value.toFixed(2) : value}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
    <div class="footer">
      Ledger Green - Sustainability Accounting Portal
    </div>
  </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: "text/html" });
    saveAs(blob, `${report.name.replace(/\s+/g, "_")}.html`);
    toast.success("HTML file downloaded");
  };

  const viewReport = (report) => {
    setSelectedReport(report);
    setViewDialogOpen(true);
  };

  const getReportIcon = (type) => {
    const reportType = REPORT_TYPES.find((t) => t.value === type);
    return reportType ? reportType.icon : FileText;
  };

  const getReportColor = (type) => {
    switch (type) {
      case "carbon_summary":
        return "bg-emerald-50 text-emerald-600";
      case "csrd_compliance":
        return "bg-blue-50 text-blue-600";
      case "supplier_analysis":
        return "bg-purple-50 text-purple-600";
      default:
        return "bg-gray-50 text-gray-600";
    }
  };

  const openCreateDialog = (type = "") => {
    setFormData({ ...formData, type });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-8" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#1a2e22] font-['Outfit'] tracking-tight">
            Reports
          </h1>
          <p className="text-[#5c6b63] mt-2">
            Generate and export sustainability reports
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary flex items-center gap-2" data-testid="create-report-btn">
              <Plus className="w-4 h-4" />
              Create Report
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Create New Report</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Report Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Q4 2024 Carbon Report"
                  className="h-11 bg-[#f2f7f4]/50 border-transparent focus:border-[#1a2e22]/20 focus:bg-white"
                  data-testid="report-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Report Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger className="h-11" data-testid="report-type-select">
                    <SelectValue placeholder="Select report type" />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={formData.period_start}
                    onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                    className="h-11 bg-[#f2f7f4]/50 border-transparent focus:border-[#1a2e22]/20 focus:bg-white"
                    data-testid="report-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={formData.period_end}
                    onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                    className="h-11 bg-[#f2f7f4]/50 border-transparent focus:border-[#1a2e22]/20 focus:bg-white"
                    data-testid="report-end-date"
                  />
                </div>
              </div>
              <Button
                onClick={handleCreateReport}
                disabled={creating}
                className="w-full btn-accent h-11"
                data-testid="submit-report-btn"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Generate Report"
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Report Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {REPORT_TYPES.map((type) => (
          <Card
            key={type.value}
            className="cursor-pointer hover:shadow-md transition-shadow border-2 border-transparent hover:border-[#00c853]/30"
            onClick={() => openCreateDialog(type.value)}
            data-testid={`report-type-${type.value}`}
          >
            <CardContent className="p-6">
              <div className={`w-12 h-12 rounded-xl ${getReportColor(type.value)} flex items-center justify-center mb-4`}>
                <type.icon className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-[#1a2e22] font-['Outfit']">{type.label}</h3>
              <p className="text-sm text-[#5c6b63] mt-1">{type.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Reports List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#1a2e22] font-['Outfit']">
            Generated Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#1a2e22] animate-spin" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-[#c8d4cc] mx-auto mb-4" />
              <p className="text-[#5c6b63] mb-2">No reports generated yet</p>
              <p className="text-sm text-[#5c6b63]">
                Click on a report type above to create your first report
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => {
                const Icon = getReportIcon(report.type);
                return (
                  <div
                    key={report.id}
                    className="flex items-center justify-between p-4 border border-[#e0e7e3] rounded-xl hover:bg-[#f2f7f4] transition-colors"
                    data-testid={`report-${report.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg ${getReportColor(report.type)} flex items-center justify-center`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-[#1a2e22]">{report.name}</p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#5c6b63]">
                          {report.company_name && (
                            <span className="font-semibold text-[#00c853]" data-testid={`report-company-${report.id}`}>
                              {report.company_name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {report.period_start} - {report.period_end}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewReport(report)}
                        className="flex items-center gap-1"
                        data-testid={`view-${report.id}`}
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportToExcel(report)}
                        className="flex items-center gap-1"
                        data-testid={`export-excel-${report.id}`}
                      >
                        <FileSpreadsheet className="w-4 h-4" />
                        Excel
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportToPDF(report)}
                        className="flex items-center gap-1"
                        data-testid={`export-pdf-${report.id}`}
                      >
                        <File className="w-4 h-4" />
                        PDF
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportToHTML(report)}
                        className="flex items-center gap-1"
                        data-testid={`export-html-${report.id}`}
                      >
                        <FileText className="w-4 h-4" />
                        HTML
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Report Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-3">
              {selectedReport && (
                <>
                  <div className={`w-8 h-8 rounded-lg ${getReportColor(selectedReport.type)} flex items-center justify-center`}>
                    {(() => {
                      const Icon = getReportIcon(selectedReport.type);
                      return <Icon className="w-4 h-4" />;
                    })()}
                  </div>
                  {selectedReport.name}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="mt-4 space-y-6">
              {/* Report Meta */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#5c6b63]" data-testid="view-report-meta">
                <span className="font-semibold text-[#00c853]">
                  {getReportCompany(selectedReport)}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {selectedReport.period_start} - {selectedReport.period_end}
                </span>
                <span>
                  Generated: {new Date(selectedReport.created_at).toLocaleString()}
                </span>
              </div>

              {/* Report Data */}
              <div className="border border-[#e0e7e3] rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#f2f7f4]">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#1a2e22]">Metric</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-[#1a2e22]">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(selectedReport.data).map(([key, value], idx) => (
                      <tr key={key} className={idx % 2 === 0 ? "bg-white" : "bg-[#f2f7f4]/50"}>
                        <td className="px-4 py-3 text-sm text-[#1a2e22]">
                          {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-mono font-medium text-[#1a2e22]">
                          {typeof value === "number" ? value.toFixed(2) : value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Export Buttons */}
              <div className="flex gap-3 pt-4 border-t border-[#e0e7e3]">
                <Button
                  onClick={() => exportToExcel(selectedReport)}
                  variant="outline"
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Export Excel
                </Button>
                <Button
                  onClick={() => exportToPDF(selectedReport)}
                  variant="outline"
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <File className="w-4 h-4" />
                  Export PDF
                </Button>
                <Button
                  onClick={() => exportToHTML(selectedReport)}
                  variant="outline"
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Export HTML
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
